import { Component, OnInit, ViewChild } from '@angular/core';
import { DesktopSidebarComponent } from "../../../../shared/components/desktop-sidebar/desktop-sidebar.component";
import { DesktopTopbarComponent } from "../../../../shared/components/desktop-topbar/desktop-topbar.component";
import { MobileTopbarComponent } from "../../../../shared/components/mobile-topbar/mobile-topbar.component";
import { TabButtonComponent } from "../../../../shared/components/tab-button/tab-button.component";
import { UserBannerComponent } from "../../components/user-banner/user-banner.component";
import { LabeledInputComponent } from "../../../../shared/components/labeled-input/labeled-input.component";
import { CustomDropdownComponent } from "../../../../shared/components/custom-dropdown/custom-dropdown.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { combineLatest, firstValueFrom } from 'rxjs';
import { ToastComponent } from "../../../../shared/components/toast/toast.component";
import { UserDataService } from '../../../../core/services/user-data.service';
import { AuthValidatorService } from '../../../../core/services/validators/auth-validator.service';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { UserService } from '../../../../core/api/user.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomFooterComponent } from "../../../../shared/components/custom-footer/custom-footer.component";
import { UiLanguage } from '../../../../core/interfaces/user-interfaces';
import { LanguageService } from '../../../../core/services/language.service';
import { DeleteAccountDialogComponent } from "../../components/dialogs/delete-account-dialog/delete-account-dialog.component";
import { Router } from '@angular/router';

@Component({
  selector: 'app-settings-my-profile-page',
  standalone: true,
  imports: [DesktopSidebarComponent, DesktopTopbarComponent, MobileTopbarComponent, TabButtonComponent, UserBannerComponent, LabeledInputComponent, CustomDropdownComponent, TranslocoPipe, ToastComponent, CustomFooterComponent, DeleteAccountDialogComponent],
  templateUrl: './settings-my-profile-page.component.html',
  styleUrl: './settings-my-profile-page.component.css'
})
export class SettingsMyProfilePageComponent implements OnInit {
  selectedSidebarItemId: number = 13;

  // Text Inputs
  firstNameInput: string = '';
  firstNameInputError: string | null = null;

  lastNameInput: string = '';
  lastNameInputError: string | null = null;

  passwordInput: string = '';
  passwordInputError: string | null = null;

  // Dropdown Menu
  rolesOptions: string[] = [];
  selectedRole: number | undefined;
  serverRoleOptions: string[] = [];

  // Language dropdown
  languageOptions: string[] = [];
  selectedLanguage: number | undefined;

  // Edit
  editingInputDisabled: boolean = true;
  isUserEditing: boolean = false;

  // Google Auth
  toggleButton: boolean = false;

  // Save clicked state
  saveClicked: boolean = false;

  // Translation
  clickEditTitle = 'Click the Edit button to modify the data, then save your changes.';

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  // Delete Account
  @ViewChild('deleteAccountDialog') deleteAccountDialog!: DeleteAccountDialogComponent;
  isDeleting: boolean = false;


  constructor(private translocoService: TranslocoService, private userData: UserDataService, private authValidator: AuthValidatorService, private apiError: ApiErrorService, private userService: UserService, private languageService: LanguageService, private router: Router) { }

  ngOnInit(): void {
    const roleKeys = [
      'shared.roles.admin',
      'shared.roles.manager',
      'shared.roles.sr_manager',
      'shared.roles.director'
    ];

    combineLatest(roleKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.rolesOptions = translations;
    });

    combineLatest([
      this.translocoService.selectTranslate('admin.click_edit')
    ]).subscribe((
      [
        clickEditTranslate
      ]
    ) => {
      this.clickEditTitle = clickEditTranslate;
    });

    // Populate language options
    this.languageOptions = Object.values(UiLanguage);
    this.selectedLanguage = this.languageOptions.indexOf(this.userData.getUser()?.settings?.ui_language ?? '');

    // Populate with user data
    this.firstNameInput = this.userData.getUser()?.first_name ?? '';
    this.lastNameInput = this.userData.getUser()?.last_name ?? '';

    this.serverRoleOptions = ["ADMIN", "MANAGER", "SR_MANAGER", "DIRECTOR"];
    this.selectedRole = this.serverRoleOptions.indexOf(this.userData.getUser()?.role ?? '');
  }

  async editSaveButtonClick() {
    if (this.saveClicked) {
      return;
    }

    this.editingInputDisabled = !this.editingInputDisabled;
    this.isUserEditing = !this.isUserEditing;

    if (this.isUserEditing) {
      return;
    }

    // Save data if user is done with editing
    this.saveClicked = true;

    // Trim inputs
    const firstNameInputTrim = this.firstNameInput.trim();
    const lastNameInputTrim = this.lastNameInput.trim();
    const passwordInputTrim = this.passwordInput.trim();

    // Validate inputs
    const firstNameError = this.authValidator.validateFirstName(firstNameInputTrim);
    const lastNameError = this.authValidator.validateLastName(lastNameInputTrim);
    const passwordError = this.authValidator.validatePassword(passwordInputTrim);

    this.firstNameInputError = firstNameError;
    this.lastNameInputError = lastNameError;

    // Validate password only if not empty
    if (passwordInputTrim.length !== 0 && passwordError) {
      this.passwordInputError = passwordError;
      this.saveClicked = false;

      this.editingInputDisabled = false;
      this.isUserEditing = true;
      return;
    }

    if (firstNameError || lastNameError) {
      this.saveClicked = false;

      this.editingInputDisabled = false;
      this.isUserEditing = true;
      return;
    }

    this.passwordInputError = '';

    const finalRole = this.selectedRole === undefined ? 'UNDEFINED' : this.serverRoleOptions.at(this.selectedRole!);

    // Update user data
    let userResponse = undefined;

    var user = this.userData.getUser();
    if (!user) {
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage("UNKNOWN-CRID"), 'error', 8000, false);
      return;
    }

    user.first_name = firstNameInputTrim;
    user.last_name = lastNameInputTrim;
    user.role = finalRole!;
    user.password = (passwordInputTrim.length === 0) ? null : passwordInputTrim;

    // UI Language
    const finalLanguage = this.selectedLanguage === undefined ? UiLanguage.EN : this.languageOptions.at(this.selectedLanguage!);
    if (user.settings) {
      user.settings.ui_language = finalLanguage!;
    }

    try {
      userResponse = await firstValueFrom(this.userService.updateUser(user));
      if (user.settings) {
        await firstValueFrom(this.userService.updateUserSettings(user.settings.id, user.settings));
      }

      // Update user data
      user.password = null;
      this.userData.setUser(user);

    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.saveClicked = false;
      this.editingInputDisabled = false;
      this.isUserEditing = true;
      return;
    }
    this.saveClicked = false;
    this.toast.showToast('shared.success', 'shared.toast.data_updated', 'success', 4000, true);
    this.languageService.setLanguage(finalLanguage!, true);
  }

  async deleteAccount() {
    if (this.isDeleting) { return; }

    var confirmed = await this.deleteAccountDialog.openModal();

    if (!confirmed) {
      return;
    }

    this.isDeleting = true;

    try {
      await firstValueFrom(this.userService.deleteAccount());

      this.router.navigate(['/account-deleted']);
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';

        if (error.error.code === 'SVC-4000') {
          this.router.navigate(['/account-deleted']);
          return;
        }
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }
    this.isDeleting = false;
  }
}
